import React, { useState, FormEvent, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";

// HOOKS
import { useAuthContext } from "../../../hooks/context/AuthContext";

// COMPONENTS
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import { Link } from "react-router-dom";
import { validate as validateEmail } from "email-validator";

// STYLES
import "./LoginOrCreateUser.scss";

interface ILoginOrCreateUserProps {
    className?: string;
    authStep: AuthFlowStep;
}

interface ILoginButtonWIthAPIStateProps extends ILoginOrCreateUserProps {
    isLoading: boolean;
}

export enum AuthFlowStep {
    SignIn = "SIGN_IN",
    CreateUser = "CREATE_USER",
}

const LoginButtonWithAPIState: React.FC<ILoginButtonWIthAPIStateProps> = ({
    className = "",
    isLoading,
    authStep,
}) => {
    if (isLoading) {
        return (
            <Button
                type="submit"
                className={`LoadingButton mt-4 ${className}`}
                disabled={true}
            >
                <Spinner animation="grow" size="sm" /> Loading...
            </Button>
        );
    }

    return (
        <Button type="submit" className="SigninButton mt-4">
            {authStep === AuthFlowStep.SignIn ? "Sign In" : "Create Account"}
        </Button>
    );
};

const LoginOrCreateUser: React.FC<ILoginOrCreateUserProps> = ({
    className = "",
    authStep,
}: ILoginOrCreateUserProps) => {
    const navigate = useNavigate();
    const {
        isSignedIn,
        isAuthResolved,
        isLoading,
        creatingUserThatAlreadyExists,
        entitlementResolved,
        //postCreateUser,
        postLoginUser,
    } = useAuthContext();

    const [email, setEmail] = useState<string>("");
    const [emailIsValid, setEmailIsValid] = useState<boolean>(true);
    const [password, setPassword] = useState<string>("");
    const [passwordIsValid, setPasswordIsValid] = useState<boolean>(true);
    const [tempErrorState, setTempErrorState] = useState<string>("");

    useEffect(() => {
        if (isAuthResolved && isSignedIn && entitlementResolved) {
            window.location.href = "/dashboard"; // HACK - avoid mobile CSS side effects
        }
    }, [isAuthResolved, isSignedIn, entitlementResolved]);

    useEffect(() => {
        if (creatingUserThatAlreadyExists === true) {
            setTempErrorState("An account with this email already exists");
        }
    }, [creatingUserThatAlreadyExists]);

    const handleLogin = async (
        e: FormEvent<HTMLFormElement>,
    ): Promise<void> => {
        e.preventDefault();

        const isEmailValid = validateEmail(email);
        const isPasswordValid =
            (authStep === AuthFlowStep.CreateUser && password.length >= 6) ||
            (authStep === AuthFlowStep.SignIn && !!password.trim());

        setEmailIsValid(isEmailValid);
        setPasswordIsValid(isPasswordValid);

        if (!isEmailValid || !isPasswordValid) {
            if (authStep === AuthFlowStep.CreateUser && password.length < 6) {
                setTempErrorState(
                    "Your password must contain at least 6 characters",
                );
            } else {
                // email is validated by bootstrap so only the invalid password
                // will trigger this - the blank password should also handled by
                // bootstrap
                setTempErrorState("Invalid Credentials");
            }
            return;
        }

        setTempErrorState("");

        try {
            if (authStep === AuthFlowStep.CreateUser) {
                console.warn("Unsupported attempt to create user");
                //await postCreateUser(email, password);
            } else {
                await postLoginUser(email, password);
            }
        } catch (error) {
            console.error("ERROR logging in / creating user", error);
            //alert(error instanceof Error ? error.message : String(error));
            setTempErrorState("Invalid Credentials. Please try again");
        }
    };

    return (
        <div className={`LoginOrCreateUser ${className}`}>
            <div className="UserAuthWrapper">
                <h3 className="mb-4">
                    {authStep === AuthFlowStep.CreateUser
                        ? "Create Account"
                        : "Sign In to Your Account"}
                </h3>
                <Form onSubmit={handleLogin}>
                    <Form.Group controlId="formEmail">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                            type="email"
                            placeholder="Email"
                            value={email}
                            isInvalid={!emailIsValid}
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                            ) => setEmail(e.target.value)}
                            required
                        />
                    </Form.Group>
                    <Form.Group controlId="formPassword" className="mt-3">
                        <Form.Label>Password</Form.Label>
                        <Form.Control
                            type="password"
                            placeholder="Password"
                            value={password}
                            isInvalid={
                                authStep === AuthFlowStep.CreateUser &&
                                !passwordIsValid
                            }
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                            ) => setPassword(e.target.value)}
                            required
                        />
                    </Form.Group>
                    {/* Forgot password */}
                    <div className="forgotPasswordWrapper">
                        <Link to="/forgot-password">Forgot Password?</Link>
                    </div>
                    {/* TODO - error handling */}
                    <LoginButtonWithAPIState
                        isLoading={isLoading}
                        authStep={authStep}
                    />
                    {/* {authStep === AuthFlowStep.SignIn ? (
                        <>
                            <div className="orDivider">
                                <div></div>
                                <p> or </p>
                                <div></div>
                            </div>
                            <Button
                                type="submit"
                                disabled={isLoading}
                                className="CreateAccountButton mt-4"
                                onClick={(e) => {
                                    e.preventDefault(); // block form submit
                                    setTempErrorState("");
                                    navigate("/get-started");
                                }}
                            >
                                Create Account
                            </Button>
                        </>
                    ) : (
                        <></>
                    )} */}
                    <p className="errorText">{tempErrorState}</p>
                </Form>

                <div className="getHelpWrapper">
                    <p>Having trouble logging in?</p>
                    <a href="mailto:support@instantheartrate.com?subject=Trouble%20Signing%20Into%20Web%20App">
                        Get Help
                    </a>
                </div>

                <div className="disclaimerWrapper">
                    <p>
                        By continuing, you are agreeing to our{" "}
                        <a href="https://www.instantheartrate.com/privacy-policy-and-terms-of-use#tos-section">
                            Terms of Service
                        </a>
                        &nbsp; and &nbsp;
                        <a href="https://www.instantheartrate.com/privacy-policy-and-terms-of-use#privacy-policy-section">
                            Privacy Policy
                        </a>
                        .
                    </p>
                </div>
                {authStep === AuthFlowStep.CreateUser ? (
                    <div className="redirectToLoginWrapper">
                        <p>
                            Already have an account?{" "}
                            <Link
                                to="/login"
                                onClick={() => setTempErrorState("")}
                            >
                                Sign In
                            </Link>
                        </p>
                    </div>
                ) : (
                    <></>
                )}
            </div>
        </div>
    );
};

export default LoginOrCreateUser;
